<template>
  <div class="flex-1 flex flex-col">
    <AppModal v-model="showAddModal" title="Add Announcement" hide-footer>
      <div class="p-4">
        <form @submit.prevent="saveAnnouncement" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Title</label>
            <input
              v-model="form.title"
              type="text"
              placeholder="Announcement title"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</label>
            <textarea
              v-model="form.description"
              placeholder="Announcement description"
              rows="3"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"
            ></textarea>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Cover image</label>
            <input
              type="file"
              accept="image/*"
              required
              class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
              @change="onAddCoverChange"
            />
            <p class="text-xs text-gray-400">Supported formats: PNG, JPG, JPEG</p>
          </div>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-save mr-1"></i>
              <span v-if="!loading">Save</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <AppModal v-model="showEditModal" title="Edit Announcement" hide-footer>
      <div class="p-4">
        <form @submit.prevent="updateAnnouncement" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Title</label>
            <input
              v-model="editForm.title"
              type="text"
              placeholder="Announcement title"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</label>
            <textarea
              v-model="editForm.description"
              placeholder="Announcement description"
              rows="3"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"
            ></textarea>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Cover image</label>
            <div v-if="editForm.coverImagePath" class="mb-2">
              <img
                :src="getCoverUrl(editForm.coverImagePath)"
                alt="Current cover"
                class="max-w-xs max-h-32 rounded border border-gray-200"
              />
            </div>
            <input
              type="file"
              accept="image/*"
              class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
              @change="onEditCoverChange"
            />
            <p class="text-xs text-gray-400">Leave empty to keep the current image</p>
          </div>
          <p class="text-xs text-gray-400 italic">This action will be logged against your account.</p>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-save mr-1"></i>
              <span v-if="!loading">Save Changes</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage mobile app banner announcements shown to users.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadAnnouncements">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button type="button" class="btn-primary" @click="openAddForm">
          <i class="fa fa-plus"></i>
          <span>Add Announcement</span>
        </button>
      </div>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <div v-if="loading && announcements.length === 0" class="py-10 text-center text-sm text-gray-400">
        Loading announcements...
      </div>
      <table v-else class="min-w-full leading-normal">
        <thead>
          <tr class="border-b border-t border-gray-200">
            <th class="t-header">#</th>
            <th class="t-header">Cover</th>
            <th class="t-header">Title</th>
            <th class="t-header">Description</th>
            <th class="t-header text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in announcements"
            :key="item.id"
            :class="idx % 2 ? rows.even : rows.odd"
          >
            <td class="t-body">{{ idx + 1 }}</td>
            <td class="t-body">
              <img
                v-if="item.coverImagePath"
                :src="getCoverUrl(item.coverImagePath)"
                :alt="item.title"
                class="h-12 w-20 object-cover rounded border border-gray-200"
              />
              <span v-else class="text-xs text-gray-400">—</span>
            </td>
            <td class="t-body font-semibold">{{ item.title }}</td>
            <td class="t-body max-w-md truncate" :title="item.description">{{ item.description }}</td>
            <td class="t-body">
              <div class="flex gap-2 justify-center">
                <button type="button" class="btn-sm-primary" title="Edit" @click="openEditForm(item)">
                  <i class="fa fa-pencil"></i>
                </button>
                <button type="button" class="btn-sm-danger" title="Delete" @click="confirmDelete(item)">
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="announcements.length === 0 && !loading">
            <td colspan="5" class="text-center py-6 text-sm text-gray-400">No announcements found</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import AppModal from "@/components/ui/AppModal.vue";
import { useConfirmDialog, useToast } from "@/composables";
import {
  buildAnnouncementFormData,
  createAnnouncement,
  deleteAnnouncement,
  extractErrorMessage,
  fetchAnnouncements,
  getAnnouncementCoverUrl,
  updateAnnouncement as updateAnnouncementRequest,
} from "@/domains/home/services/configurations/views/mobile/announcementsApi";

export default {
  name: "Announcements",
  components: { AppModal },
  setup() {
    const { successToast, errorToast, warningToast } = useToast();
    const { showConfirm } = useConfirmDialog();
    return { successToast, errorToast, warningToast, showConfirm };
  },
  data() {
    return {
      showAddModal: false,
      showEditModal: false,
      loading: false,
      announcements: [],
      rows: {
        odd: "border-t border-gray-200 hover:bg-gray-10",
        even: "border-t border-gray-200 bg-gray-10",
      },
      form: {
        title: "",
        description: "",
        coverImage: null,
      },
      editForm: {
        id: "",
        title: "",
        description: "",
        coverImage: null,
        coverImagePath: null,
      },
    };
  },
  async mounted() {
    await this.loadAnnouncements();
  },
  methods: {
    getCoverUrl(path) {
      return getAnnouncementCoverUrl(path);
    },
    async loadAnnouncements() {
      this.loading = true;
      try {
        this.announcements = await fetchAnnouncements();
      } catch (error) {
        this.announcements = [];
        this.errorToast("Error", extractErrorMessage(error, "Could not load announcements"));
      } finally {
        this.loading = false;
      }
    },
    openAddForm() {
      this.resetForm();
      this.showAddModal = true;
    },
    openEditForm(item) {
      this.editForm = {
        id: item.id,
        title: item.title,
        description: item.description,
        coverImage: null,
        coverImagePath: item.coverImagePath ?? null,
      };
      this.showEditModal = true;
    },
    resetForm() {
      this.form = { title: "", description: "", coverImage: null };
    },
    onAddCoverChange(event) {
      const file = event.target.files?.[0];
      this.form.coverImage = file ?? null;
    },
    onEditCoverChange(event) {
      const file = event.target.files?.[0];
      this.editForm.coverImage = file ?? null;
    },
    async saveAnnouncement() {
      const title = this.form.title.trim();
      const description = this.form.description.trim();
      if (!title || !description) {
        this.warningToast("Validation", "Enter title and description");
        return;
      }
      if (!this.form.coverImage) {
        this.warningToast("Validation", "Select a cover image");
        return;
      }
      this.loading = true;
      try {
        const formData = buildAnnouncementFormData({
          title,
          description,
          coverImage: this.form.coverImage,
        });
        await createAnnouncement(formData);
        this.successToast("Success", "Announcement added successfully");
        this.showAddModal = false;
        this.resetForm();
        await this.loadAnnouncements();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Could not add announcement"));
      } finally {
        this.loading = false;
      }
    },
    async updateAnnouncement() {
      const title = this.editForm.title.trim();
      const description = this.editForm.description.trim();
      if (!title || !description) {
        this.warningToast("Validation", "Enter title and description");
        return;
      }
      this.loading = true;
      try {
        const formData = buildAnnouncementFormData({
          title,
          description,
          coverImage: this.editForm.coverImage,
        });
        await updateAnnouncementRequest(this.editForm.id, formData);
        this.successToast("Success", "Announcement updated successfully");
        this.showEditModal = false;
        await this.loadAnnouncements();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Could not update announcement"));
      } finally {
        this.loading = false;
      }
    },
    async confirmDelete(item) {
      const confirmed = await this.showConfirm(
        `Delete announcement "${item.title}"?`,
        {
          title: "Delete Announcement",
          variant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
        },
      );
      if (!confirmed) return;
      this.loading = true;
      try {
        await deleteAnnouncement(item.id);
        this.successToast("Success", "Announcement deleted successfully");
        await this.loadAnnouncements();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Could not delete announcement"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
